import { Box, IconButton, TextField, MenuItem, Collapse, Tooltip } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useState, useEffect } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { withAsyncPaginate } from 'react-select-async-paginate'
import Creatable from 'react-select/creatable'
import { getCPDataWithoutParams } from 'src/clinic/services'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DeleteIcon from '@mui/icons-material/Delete'
import EditNoteIcon from '@mui/icons-material/EditNote'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import DayjsDatePicker from 'src/mentor/components/commonReportComponents/components/datePicker/DayjsDatePicker'

dayjs.extend(utc)

const CreatableAsyncPaginate = withAsyncPaginate(Creatable)

// Common styles for select components
const commonStyles = {
  control: (baseStyles, state) => ({
    ...baseStyles,
    borderColor: state.isFocused ? '#6779e3' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(103, 121, 227, 0.2)' : 'none',
    borderRadius: '10px',
    minHeight: '44px',
    '&:hover': {
      borderColor: '#6779e3',
    },
  }),
  menu: (baseStyles) => ({
    ...baseStyles,
    borderRadius: '10px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    zIndex: 9999,
  }),
  menuPortal: (baseStyles) => ({
    ...baseStyles,
    zIndex: 9999,
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    backgroundColor: state.isSelected ? '#6779e3' : state.isFocused ? 'rgba(103, 121, 227, 0.1)' : 'white',
    color: state.isSelected ? 'white' : '#1e293b',
    padding: '10px 14px',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: 'rgba(103, 121, 227, 0.2)',
    },
  }),
  multiValue: (baseStyles) => ({
    ...baseStyles,
    backgroundColor: 'rgba(103, 121, 227, 0.1)',
    borderRadius: '6px',
  }),
  multiValueLabel: (baseStyles) => ({
    ...baseStyles,
    color: '#4f46e5',
    fontWeight: 500,
  }),
  multiValueRemove: (baseStyles) => ({
    ...baseStyles,
    color: '#6779e3',
    '&:hover': {
      backgroundColor: 'rgba(103, 121, 227, 0.2)',
      color: '#4f46e5',
    },
  }),
  placeholder: (baseStyles) => ({
    ...baseStyles,
    color: '#94a3b8',
  }),
}

// Styled components
const ComplaintCard = styled(Box)(({ theme }) => ({
  padding: '16px',
  backgroundColor: '#f8fafc',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  marginTop: '12px',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: '#cbd5e1',
  },
}))

const ComplaintHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
}))

const ComplaintTitle = styled(Box)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '15px',
  color: '#1e293b',
  flex: 1,
}))

const FieldsContainer = styled(Box)(({ theme }) => ({
  marginTop: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}))

const FieldRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}))

const FieldLabel = styled(Box)(({ theme }) => ({
  fontSize: '13px',
  color: '#64748b',
  marginBottom: '6px',
  fontWeight: 500,
}))

const DurationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}))

// API call to load complaints
const loadOptionsComplaints = async (search, loadedOptions, { page, clinicId, clinicBrandId }) => {
  const response = await getCPDataWithoutParams(
    `/api/secure/clinic-practitioner/health-complaint/list?search=${search}&clinicId=${clinicId}${clinicBrandId ? `&clinicBrandId=${clinicBrandId}` : ''}&page=${page}&pageSize=5`,
  )
  var hasMore = true
  var optionsArray = []

  if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
    optionsArray = response?.data?.map((option) => ({
      ...option,
      value: option?.id || option?.title,
      label: option?.title,
    }))
  } else {
    hasMore = false
  }

  return {
    options: optionsArray,
    hasMore,
    additional: {
      page: page + 1,
      clinicId,
      clinicBrandId,
    },
  }
}

// Individual Complaint Item component with expandable fields
const ComplaintItem = ({ 
  complaint, 
  index, 
  onUpdate, 
  onRemove,
  expanded,
  onToggleExpand,
}) => {
  const title = complaint?.title || complaint?.label || complaint?.value || ''
  
  const handleFieldChange = (field, value) => {
    onUpdate(index, { ...complaint, [field]: value })
  }

  const handleDurationChange = (field, value) => {
    if (field === 'durationSince') {
      // Convert to number or null
      const numValue = value === '' ? null : parseInt(value, 10)
      handleFieldChange('durationSince', isNaN(numValue) ? null : numValue)
    } else {
      handleFieldChange(field, value)
    }
  }

  return (
    <ComplaintCard>
      <ComplaintHeader>
        <ComplaintTitle>{title}</ComplaintTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Tooltip title={expanded ? 'Collapse details' : 'Expand details'}>
            <IconButton 
              size="small" 
              onClick={() => onToggleExpand(index)}
              sx={{ 
                color: '#64748b',
                '&:hover': { 
                  backgroundColor: 'rgba(103, 121, 227, 0.1)',
                  color: '#6779e3',
                },
              }}
            >
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove complaint">
            <IconButton 
              size="small" 
              onClick={() => onRemove(index)}
              sx={{ 
                color: '#94a3b8',
                '&:hover': { 
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: '#ef4444',
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </ComplaintHeader>
      
      <Collapse in={expanded}>
        <FieldsContainer>
          {/* Note field */}
          <Box>
            <FieldLabel>
              <EditNoteIcon sx={{ fontSize: 16, marginRight: '4px', verticalAlign: 'middle' }} />
              Notes
            </FieldLabel>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              value={complaint?.note || ''}
              onChange={(e) => handleFieldChange('note', e.target.value)}
              placeholder="Add notes about this complaint..."
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6779e3',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6779e3',
                  },
                },
              }}
            />
          </Box>

          <FieldRow>
            {/* Onset Date field */}
            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <FieldLabel>
                <CalendarMonthIcon sx={{ fontSize: 16, marginRight: '4px', verticalAlign: 'middle' }} />
                Onset Date
              </FieldLabel>
              <DayjsDatePicker
                date={complaint?.onsetTime ? dayjs.utc(complaint.onsetTime).local() : null}
                onChange={(date) => {
                  // Store the picked calendar day as UTC midnight (matches the other onset fields and
                  // avoids a ±1-day shift when read back as dayjs.utc(...).local()).
                  handleFieldChange(
                    'onsetTime',
                    date ? dayjs.utc(date.format('YYYY-MM-DD')).format('YYYY-MM-DD HH:mm:ss') : null,
                  )
                }}
                format="DD MMM YYYY"
                placeholder="Select onset date"
                calendarProps={{ maxDate: dayjs().toDate() }}
                buttonProps={{
                  fullWidth: true,
                  sx: {
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    color: '#1e293b',
                    backgroundColor: '#fff',
                    borderColor: '#e5e7eb',
                    borderRadius: '8px',
                  },
                }}
              />
            </Box>

            {/* Duration Since field */}
            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <FieldLabel>
                <AccessTimeIcon sx={{ fontSize: 16, marginRight: '4px', verticalAlign: 'middle' }} />
                Duration
              </FieldLabel>
              <DurationContainer>
                <TextField
                  type="number"
                  value={complaint?.durationSince ?? ''}
                  onChange={(e) => handleDurationChange('durationSince', e.target.value)}
                  placeholder="Enter"
                  size="small"
                  inputProps={{ min: 0 }}
                  sx={{
                    width: '100px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                    },
                  }}
                />
                <TextField
                  select
                  value={complaint?.durationType || 'days'}
                  onChange={(e) => handleFieldChange('durationType', e.target.value)}
                  size="small"
                  sx={{
                    minWidth: '100px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                    },
                  }}
                >
                  <MenuItem value="days">Days</MenuItem>
                  <MenuItem value="weeks">Weeks</MenuItem>
                  <MenuItem value="months">Months</MenuItem>
                  <MenuItem value="years">Years</MenuItem>
                </TextField>
              </DurationContainer>
            </Box>
          </FieldRow>
        </FieldsContainer>
      </Collapse>
    </ComplaintCard>
  )
}

/**
 * PresentingComplaintsSection - Reusable component for managing presenting complaints
 * with note, onsetTime, durationSince, and durationType fields.
 * 
 * @param {string} clinicId - The clinic ID for API calls
 * @param {string} clinicBrandId - Optional clinic brand ID
 * @param {Array} selectedComplaints - Array of selected complaint objects
 * @param {Function} setSelectedComplaints - Setter for selected complaints
 * @param {boolean} showTitle - Whether to show the section title (default: false, handled externally)
 */
export const PresentingComplaintsSection = ({
  clinicId,
  clinicBrandId,
  selectedComplaints = [],
  setSelectedComplaints,
  showTitle = false,
}) => {
  // Track which complaints are expanded - default to all expanded
  const [expandedItems, setExpandedItems] = useState({})

  // Initialize all existing complaints as expanded when data loads
  useEffect(() => {
    if (Array.isArray(selectedComplaints) && selectedComplaints.length > 0) {
      const initialExpanded = {}
      selectedComplaints.forEach((_, index) => {
        initialExpanded[index] = true
      })
      setExpandedItems(prev => {
        // Only update if there are new items to expand
        const hasChanges = selectedComplaints.some((_, index) => prev[index] === undefined)
        if (hasChanges) {
          return { ...prev, ...initialExpanded }
        }
        return prev
      })
    }
  }, [selectedComplaints?.length])

  const handleComplaintsChange = (newSelection) => {
    if (!newSelection) {
      setSelectedComplaints([])
      setExpandedItems({})
      return
    }

    // Map new selections to include default field values
    const mappedSelection = newSelection.map((item, index) => {
      // Check if this item already exists in selectedComplaints
      const existingItem = selectedComplaints?.find(
        (c) => (c.id && c.id === item.id) || (c.value === item.value)
      )
      
      if (existingItem) {
        // Preserve existing fields
        return { ...existingItem, ...item }
      }
      
      // New item - initialize with defaults
      return {
        ...item,
        note: item.note || '',
        onsetTime: item.onsetTime || null,
        durationSince: item.durationSince ?? null,
        durationType: item.durationType || 'days',
      }
    })

    // Expand all newly added items by default
    const newExpanded = { ...expandedItems }
    mappedSelection.forEach((_, index) => {
      if (newExpanded[index] === undefined) {
        newExpanded[index] = true // Default to expanded
      }
    })
    setExpandedItems(newExpanded)

    setSelectedComplaints(mappedSelection)
  }

  const handleUpdateComplaint = (index, updatedComplaint) => {
    const newComplaints = [...selectedComplaints]
    newComplaints[index] = updatedComplaint
    setSelectedComplaints(newComplaints)
  }

  const handleRemoveComplaint = (index) => {
    const newComplaints = selectedComplaints.filter((_, i) => i !== index)
    setSelectedComplaints(newComplaints)
    
    // Update expanded state
    const newExpanded = { ...expandedItems }
    delete newExpanded[index]
    // Reindex remaining items
    const reindexed = {}
    Object.keys(newExpanded).forEach(key => {
      const keyNum = parseInt(key, 10)
      if (keyNum > index) {
        reindexed[keyNum - 1] = newExpanded[key]
      } else {
        reindexed[keyNum] = newExpanded[key]
      }
    })
    setExpandedItems(reindexed)
  }

  const handleToggleExpand = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Dropdown selector */}
      <CreatableAsyncPaginate
        id="presentingComplaintsSelection"
        placeholder="Search and select complaints..."
        isMulti={true}
        isClearable={true}
        isSearchable={true}
        defaultOptions={false}
        loadOptions={loadOptionsComplaints}
        getOptionValue={(option) => option?.value || option?.id}
        getOptionLabel={(option) => option?.label || option?.title}
        value={selectedComplaints}
        onChange={handleComplaintsChange}
        debounceTimeout={300}
        additional={{ page: 1, clinicId, clinicBrandId }}
        styles={commonStyles}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />

      {/* Display selected complaints with expandable fields */}
      {Array.isArray(selectedComplaints) && selectedComplaints.length > 0 && (
        <Box sx={{ marginTop: '8px' }}>
          {selectedComplaints.map((complaint, index) => (
            <ComplaintItem
              key={complaint?.id || complaint?.value || index}
              complaint={complaint}
              index={index}
              onUpdate={handleUpdateComplaint}
              onRemove={handleRemoveComplaint}
              expanded={expandedItems[index] !== false} // Default to expanded (true) unless explicitly collapsed
              onToggleExpand={handleToggleExpand}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

// Helper function to convert complaints array for API submission
export function getComplaintsArrayForSubmission(complaintsArray, clinicId) {
  const toReturn = []
  if (Array.isArray(complaintsArray) && complaintsArray.length > 0) {
    complaintsArray.forEach((complaintItem) => {
      if (complaintItem?.label || complaintItem?.title || complaintItem?.id || complaintItem?.value) {
        const fullComplaint = {
          // Preserve all original fields
          ...complaintItem,
          // Ensure required fields are present
          // Note: apiParam will be overridden in save functions for ScreeningReport and TherapyReport
          apiParam: complaintItem?.apiParam || 'health-complaint',
          title: complaintItem?.title || complaintItem?.label || complaintItem?.value,
          // Include the new fields
          note: complaintItem?.note || null,
          onsetTime: complaintItem?.onsetTime || null,
          durationSince: complaintItem?.durationSince ?? null,
          durationType: complaintItem?.durationType || 'days',
        }
        if (clinicId) {
          fullComplaint.clinicId = clinicId
        }
        toReturn.push(fullComplaint)
      }
    })
  }
  return toReturn
}

// Helper function to convert server data to form format
export function getInitialDataForSettingExistingSelectedComplaints(complaintsFromServer) {
  const toReturn = []
  if (Array.isArray(complaintsFromServer) && complaintsFromServer.length > 0) {
    complaintsFromServer.forEach((item) => {
      toReturn.push({
        ...item,
        label: item?.title || item?.label,
        value: item?.id || item?.value || item?.title,
        // Preserve all fields from server
        note: item?.note || '',
        onsetTime: item?.onsetTime || null,
        durationSince: item?.durationSince ?? null,
        durationType: item?.durationType || 'days',
      })
    })
  }
  return toReturn
}

// Component for displaying complaints in report preview/print
export const PresentingComplaintsDisplaySection = ({ selectedComplaints, showTitle = true }) => {
  if (!Array.isArray(selectedComplaints) || selectedComplaints.length === 0) {
    return null
  }

  return (
    <Box sx={{
      padding: '16px',
      backgroundColor: '#fefefe',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
    }}>
      {showTitle && (
        <Box sx={{
          fontWeight: '600',
          fontSize: '14px',
          color: '#4338ca',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: '8px',
          paddingBottom: '6px',
          borderBottom: '2px solid #e0e7ff',
        }}>
          Presenting Complaints
        </Box>
      )}
      <Box sx={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {selectedComplaints.map((complaint, index) => {
          const title = complaint?.title || complaint?.label || complaint?.value || ''
          if (!title) return null

          const hasDetails = complaint?.note || complaint?.onsetTime || complaint?.durationSince

          return (
            <Box
              key={index}
              sx={{
                padding: '12px',
                backgroundColor: '#f8fafc',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
              }}
            >
              <Box sx={{ fontWeight: 600, color: '#1e293b', marginBottom: hasDetails ? '8px' : '0' }}>
                {title}
              </Box>
              
              {complaint?.note && (
                <Box sx={{
                  marginTop: '8px',
                  fontSize: '14px',
                  color: '#334155',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                }}>
                  <strong>Note:</strong> {complaint.note}
                </Box>
              )}
              
              {(complaint?.onsetTime || complaint?.durationSince) && (
                <Box sx={{
                  marginTop: '8px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '16px',
                  fontSize: '14px',
                  color: '#334155',
                }}>
                  {complaint?.onsetTime && (
                    <Box>
                      <strong>Onset:</strong> {dayjs.utc(complaint.onsetTime).local().format('DD MMM YYYY')}
                    </Box>
                  )}
                  {complaint?.durationSince && (
                    <Box>
                      <strong>Duration:</strong> {complaint.durationSince} {complaint.durationType || 'days'}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default PresentingComplaintsSection

